import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { jobAPI, bookmarkAPI, jobSearchPreferenceAPI } from '../../services/api';
import Header from '../Header';
import Footer from '../Footer';
import BottomNavigationBar from '../BottomNavigationBar';
import JobDetailPage from './JobDetailPage';
import JobApplicationForm from './JobApplicationForm';
import JobFilters from './JobFilters';
import JobShareModal from './JobShareModal';
import ReportJobModal from './ReportJobModal';
import VacancyBookmarkModal from './VacancyBookmarkModal';
import JobDescriptionContent from './JobDescriptionContent';
import { getJobPosterLabel, getJobPosterUsername, shouldLinkJobPoster } from '../../utils/jobDisplay';
import { handleJobApply } from '../../utils/jobApply';
import './JobsPage.css';

const JobsPage = ({ onHomeClick, onCategoriesClick, onHowItWorksClick, onProfileClick, onAccountSettingsClick, onForumClick, onJobsClick, onLoginClick, onSignupClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [appliedJobIds, setAppliedJobIds] = useState(new Set()); // Set of job IDs user has applied to
  const [hiddenJobIds, setHiddenJobIds] = useState(new Set()); // Set of job IDs user has hidden/reported
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreJobs, setHasMoreJobs] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    jobType: '',
    workType: '',
    experienceLevel: '',
    location: '',
    datePosted: 'all',
    bookmarkListId: '',
    preferenceId: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    jobType: '',
    workType: '',
    experienceLevel: '',
    location: '',
    datePosted: 'all',
    bookmarkListId: '',
    preferenceId: '',
  });
  const [bookmarkLists, setBookmarkLists] = useState([]);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [savedPreferences, setSavedPreferences] = useState([]);
  const [showSavePreferenceModal, setShowSavePreferenceModal] = useState(false);
  const [preferenceTitle, setPreferenceTitle] = useState('');
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    // Reset pagination when filters change
    setCurrentPage(1);
    setJobs([]);
    fetchPublicJobs(1, true);
    if (isAuthenticated && user) {
      fetchUserApplications();
      fetchBookmarkLists();
      fetchSavedPreferences();
    }
  }, []);

  // Fetch bookmark lists on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchBookmarkLists();
      fetchSavedPreferences();
    }
  }, [isAuthenticated]);

  const fetchBookmarkLists = async () => {
    try {
      // Only fetch lists that have vacancies (from vacancy_bookmarks table)
      const response = await bookmarkAPI.getLists('vacancies');
      if (response.success) {
        setBookmarkLists(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching bookmark lists:', err);
      setBookmarkLists([]);
    }
  };

  const fetchSavedPreferences = async () => {
    try {
      setLoadingPreferences(true);
      const response = await jobSearchPreferenceAPI.getAll();
      if (response.success) {
        setSavedPreferences(response.data || []);
      } else {
        console.error('Failed to fetch saved preferences:', response);
      }
    } catch (err) {
      console.error('Error fetching saved preferences:', err);
      setSavedPreferences([]);
    } finally {
      setLoadingPreferences(false);
    }
  };

  // Fetch jobs when applied filters change
  useEffect(() => {
    // Reset pagination when filters change
    setCurrentPage(1);
    setJobs([]);
    fetchPublicJobs(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // Fetch user's applications to check which jobs they've applied to
  const fetchUserApplications = async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      const response = await jobAPI.getMyApplications({});
      if (response.success) {
        const apps = response.data?.data || response.data || [];
        // Create a Set of job IDs the user has applied to
        const jobIds = new Set(apps.map(app => app.job_id || app.job?.id).filter(Boolean));
        setAppliedJobIds(jobIds);
      }
    } catch (error) {
      console.error('Error fetching user applications:', error);
    }
  };

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle body scroll when modal is open
  useEffect(() => {
    if (showMobileModal || showFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileModal, showFilters]);

  // Handle jobId from URL when jobs are already loaded
  useEffect(() => {
    const jobIdFromUrl = searchParams.get('jobId');
    if (jobIdFromUrl && jobs.length > 0) {
      const jobToSelect = jobs.find(job => {
        const jobId = job.id.toString();
        const urlJobId = jobIdFromUrl.toString();
        return jobId === urlJobId || job.id === parseInt(jobIdFromUrl);
      });
      
      if (jobToSelect && (!selectedJob || selectedJob.id.toString() !== jobIdFromUrl)) {
        setSelectedJob(jobToSelect);
        // Check if user has applied to this job
        const hasAppliedToJob = appliedJobIds.has(jobToSelect.id);
        setHasApplied(hasAppliedToJob);
        // Remove the query parameter from URL after selecting
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, jobs.length, appliedJobIds]);

  // Update hasApplied when selectedJob or appliedJobIds changes
  useEffect(() => {
    if (selectedJob && appliedJobIds.size > 0) {
      const hasAppliedToJob = appliedJobIds.has(selectedJob.id);
      setHasApplied(hasAppliedToJob);
    }
  }, [selectedJob, appliedJobIds]);

  const fetchPublicJobs = async (page = 1, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      let response;
      
      // Check if bookmark list is selected
      if (appliedFilters.bookmarkListId && appliedFilters.bookmarkListId !== '' && isAuthenticated) {
        // Fetch bookmarked vacancies
        const apiParams = {
          list_id: appliedFilters.bookmarkListId,
          per_page: 10,
          page: page
        };

        // Add filter parameters
        if (appliedFilters.jobType && appliedFilters.jobType !== '') {
          apiParams.job_type = appliedFilters.jobType;
        }

        if (appliedFilters.workType && appliedFilters.workType !== '') {
          apiParams.work_type = appliedFilters.workType;
        }

        if (appliedFilters.experienceLevel && appliedFilters.experienceLevel !== '') {
          apiParams.experience_level = appliedFilters.experienceLevel;
        }

        if (appliedFilters.location && appliedFilters.location.trim() !== '') {
          apiParams.location = appliedFilters.location.trim();
        }

        // Handle date posted filter
        if (appliedFilters.datePosted && appliedFilters.datePosted !== 'all') {
          apiParams.date_posted = appliedFilters.datePosted;
        }

        response = await bookmarkAPI.getBookmarkedVacancies(apiParams);
      } else {
        // Build API parameters from applied filters for regular job search
        const apiParams = {
          status: 'published',
          is_active: true,
          per_page: 10,
          page: page
        };
        
        // Only exclude applied jobs if user is authenticated (for "All jobs" page)
        if (isAuthenticated && user) {
          apiParams.exclude_applied = '1';
        }

        // Add filter parameters
        if (appliedFilters.jobType && appliedFilters.jobType !== '') {
          apiParams.job_type = appliedFilters.jobType;
        }

        if (appliedFilters.workType && appliedFilters.workType !== '') {
          apiParams.work_type = appliedFilters.workType;
        }

        if (appliedFilters.experienceLevel && appliedFilters.experienceLevel !== '') {
          apiParams.experience_level = appliedFilters.experienceLevel;
        }

        if (appliedFilters.location && appliedFilters.location.trim() !== '') {
          apiParams.location = appliedFilters.location.trim();
        }

        // Handle date posted filter - calculate date range
        if (appliedFilters.datePosted && appliedFilters.datePosted !== 'all') {
          const now = new Date();
          const cutoffDate = new Date();
          
          switch (appliedFilters.datePosted) {
            case '24h':
              cutoffDate.setHours(now.getHours() - 24);
              break;
            case 'week':
              cutoffDate.setDate(now.getDate() - 7);
              break;
            case 'month':
              cutoffDate.setMonth(now.getMonth() - 1);
              break;
            default:
              break;
          }
          
          // Format date for API (YYYY-MM-DD)
          const dateString = cutoffDate.toISOString().split('T')[0];
          apiParams.created_after = dateString;
        }

        // Fetch jobs with filters from server (server-side pagination)
        console.log('Fetching jobs - Page:', page, 'Per Page: 10', 'Params:', apiParams);
        response = await jobAPI.getAll(apiParams);
      }
      
      if (response.success) {
        console.log('Pagination response:', {
          current_page: response.data?.current_page || response.pagination?.current_page,
          last_page: response.data?.last_page || response.pagination?.last_page,
          total: response.data?.total || response.pagination?.total,
          per_page: response.data?.per_page || response.pagination?.per_page,
          data_length: response.data?.data?.length || response.data?.length
        });
        // Handle paginated response
        let jobsData = [];
        let paginationInfo = null;
        
        if (response.data?.data && Array.isArray(response.data.data)) {
          jobsData = response.data.data;
          paginationInfo = response.data;
        } else if (response.pagination) {
          jobsData = response.data || [];
          paginationInfo = response.pagination;
        } else if (Array.isArray(response.data)) {
          jobsData = response.data;
        }
        
        // Don't filter out hidden jobs here - let them show with overlay
        // The backend will exclude them on next fetch, but we want to show overlay for current session
        // Jobs are already filtered on the server side (exclude_applied parameter)
        // Append or replace jobs based on reset flag
        if (reset) {
          setJobs(jobsData);
        } else {
          setJobs(prev => {
            // Combine previous jobs with new ones, avoiding duplicates
            const existingJobIds = new Set(prev.map(j => j.id));
            const newJobs = jobsData.filter(job => !existingJobIds.has(job.id));
            return [...prev, ...newJobs];
          });
        }
        
        // Update pagination state
        if (paginationInfo) {
          const totalPages = paginationInfo.last_page || Math.ceil((paginationInfo.total || 0) / 10);
          setHasMoreJobs(page < totalPages);
          setTotalJobs(paginationInfo.total || jobsData.length);
        } else {
          // If no pagination info, assume no more jobs if we got less than requested
          setHasMoreJobs(jobsData.length === 10);
        }
        
        setCurrentPage(page);
        
        // Check if there's a jobId in the URL query params
        const jobIdFromUrl = searchParams.get('jobId');
        const allJobs = reset ? jobsData : [...jobs, ...jobsData];
        
        if (jobIdFromUrl && allJobs.length > 0) {
          const jobToSelect = allJobs.find(job => job.id === parseInt(jobIdFromUrl) || job.id.toString() === jobIdFromUrl);
          if (jobToSelect) {
            setSelectedJob(jobToSelect);
            const hasAppliedToJob = appliedJobIds.has(jobToSelect.id);
            setHasApplied(hasAppliedToJob);
          } else if (allJobs.length > 0 && reset) {
            const firstJob = allJobs[0];
            setSelectedJob(firstJob);
            const hasAppliedToFirstJob = appliedJobIds.has(firstJob.id);
            setHasApplied(hasAppliedToFirstJob);
          }
        } else if (allJobs.length > 0 && reset) {
          const firstJob = allJobs[0];
          setSelectedJob(firstJob);
          const hasAppliedToFirstJob = appliedJobIds.has(firstJob.id);
          setHasApplied(hasAppliedToFirstJob);
        } else if (reset && allJobs.length === 0) {
          setSelectedJob(null);
        }
      }
    } catch (err) {
      console.error('Error fetching public jobs:', err);
      if (reset) {
        setJobs([]);
        setSelectedJob(null);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreJobs) {
      const nextPage = currentPage + 1;
      fetchPublicJobs(nextPage, false);
    }
  };

  const handleJobSelect = (job, e) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    
    if (isModifierPressed) {
      // Open job detail page in new tab
      e?.preventDefault();
      e?.stopPropagation();
      // Use share_token if available, otherwise use ID
      const jobUrl = job.share_token 
        ? `/job/${job.share_token}`
        : `/vacancies/${job.id}`;
      window.open(jobUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Normal selection - show in detail panel
    setSelectedJob(job);
    // Check if user has already applied to this job
    const hasAppliedToJob = appliedJobIds.has(job.id);
    setHasApplied(hasAppliedToJob);
    // Show modal on mobile, otherwise show in side panel
    if (isMobile) {
      setShowMobileModal(true);
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value === '' ? '' : value
    }));
    // Don't fetch jobs automatically - wait for search button click
  };

  const handleApplyFilters = () => {
    // Apply the current filters and trigger search
    setAppliedFilters({ ...filters });
    // Close mobile modal after applying
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      jobType: '',
      workType: '',
      experienceLevel: '',
      location: '',
      datePosted: 'all',
      bookmarkListId: '',
      preferenceId: '',
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    // Jobs will be fetched automatically via useEffect when appliedFilters change
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type, id: Date.now() });
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setToast(null);
    }, 5000);
  };

  const handleSavePreferenceConfirm = async () => {
    if (!preferenceTitle.trim()) {
      showToast('Please enter a title for your search preference.', 'error');
      return;
    }

    try {
      const response = await jobSearchPreferenceAPI.create({
        title: preferenceTitle.trim(),
        search_query: null, // Jobs don't have a separate search_query field in filters
        job_type: filters.jobType || null,
        work_type: filters.workType || null,
        experience_level: filters.experienceLevel || null,
        location: filters.location?.trim() || null,
        date_posted: filters.datePosted !== 'all' ? filters.datePosted : null,
        is_active: true
      });

      if (response.success) {
        setShowSavePreferenceModal(false);
        setPreferenceTitle('');
        await fetchSavedPreferences();
        showToast('Search preference saved successfully! You will be notified when new jobs match this search.', 'success');
      } else {
        showToast(response.message || 'Failed to save preference. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error saving preference:', err);
      showToast('Failed to save preference. Please try again.', 'error');
    }
  };

  const handleSavePreference = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if there are any active filters
    const hasFilters = filters.jobType || filters.workType || filters.experienceLevel || 
                       (filters.location && filters.location.trim() !== '') || 
                       (filters.datePosted && filters.datePosted !== 'all');

    if (!hasFilters) {
      showToast('Please apply at least one filter before saving a preference.', 'warning');
      return;
    }

    setShowSavePreferenceModal(true);
  };

  const handlePreferenceChange = (preferenceId) => {
    if (!preferenceId || preferenceId === '') {
      // Clear preference filters but keep bookmark filter
      const newFilters = {
        jobType: '',
        workType: '',
        experienceLevel: '',
        location: '',
        datePosted: 'all',
        bookmarkListId: filters.bookmarkListId || '',
        preferenceId: '',
      };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
      return;
    }

    // Find the selected preference
    const preference = savedPreferences.find(p => p.id.toString() === preferenceId.toString());
    if (!preference) return;

    // Apply the preference filters
    const newFilters = {
      jobType: preference.job_type || '',
      workType: preference.work_type || '',
      experienceLevel: preference.experience_level || '',
      location: preference.location || '',
      datePosted: preference.date_posted || 'all',
      bookmarkListId: filters.bookmarkListId || '', // Keep bookmark filter if active
      preferenceId: preferenceId,
    };
    
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    
    // Close mobile modal if open
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const handleBookmarkChange = (bookmarkListId) => {
    // Auto-apply bookmark filter changes immediately for better UX
    setFilters(prev => ({
      ...prev,
      bookmarkListId: bookmarkListId || ''
    }));
    setAppliedFilters(prev => ({
      ...prev,
      bookmarkListId: bookmarkListId || ''
    }));
    // Reset pagination when bookmark filter changes
    setCurrentPage(1);
    setJobs([]);
  };

  const handleApplyClick = () => {
    handleJobApply(selectedJob, { isAuthenticated, navigate, setShowApplicationForm });
  };

  const handleApplicationSuccess = () => {
    setShowApplicationForm(false);
    setHasApplied(true);
    // Add the job ID to applied jobs set
    if (selectedJob) {
      setAppliedJobIds(prev => new Set([...prev, selectedJob.id]));
    }
    fetchPublicJobs(1, true); // Refresh to get updated application count
    fetchUserApplications(); // Refresh user's applications list
  };

  // Handle job report success - mark as hidden (no reload)
  const handleJobReportSuccess = (jobId, shouldHide = true) => {
    // If hideJob is checked, mark job as hidden immediately (overlay will show on the job item)
    if (shouldHide) {
      setHiddenJobIds(prev => new Set([...prev, jobId]));
      // Don't clear selection - user might want to view the job details
      // Don't reload jobs - keep the list as is with overlay showing
    }
    // Modal will close automatically via ReportJobModal's setTimeout
    // No need to refresh the jobs list
  };

  // Handle unhide job - remove from hidden list
  const handleUnhideJob = async (jobId) => {
    try {
      const response = await jobAPI.unhide(jobId);
      if (response.success) {
        // Remove from hiddenJobIds to hide the overlay
        setHiddenJobIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
      } else {
        console.error('Failed to unhide job:', response.message);
      }
    } catch (error) {
      console.error('Error unhiding job:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) {
      return null;
    }
    
    const currency = job.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
    const period = job.salary_period === 'yearly' ? 'year' : 
                   job.salary_period === 'monthly' ? 'month' :
                   job.salary_period === 'hourly' ? 'hour' : job.salary_period;
    
    if (job.salary_min && job.salary_max) {
      return `${symbol}${job.salary_min.toLocaleString()} - ${symbol}${job.salary_max.toLocaleString()}/${period}`;
    } else if (job.salary_min) {
      return `${symbol}${job.salary_min.toLocaleString()}+/${period}`;
    }
    return null;
  };

  // Use provided handlers or create defaults with navigate
  const handleHomeClick = onHomeClick || (() => navigate('/'));
  const handleCategoriesClick = onCategoriesClick || (() => navigate('/categories'));
  const handleHowItWorksClick = onHowItWorksClick || (() => navigate('/#how-it-works'));
  const handleProfileClick = onProfileClick || (() => navigate('/profile'));
  const handleAccountSettingsClick = onAccountSettingsClick || (() => navigate('/settings'));
  const handleForumClick = onForumClick || (() => navigate('/forum'));
  const handleJobsClick = onJobsClick || (() => navigate('/vacancies'));

  return (
    <div className="jobs-page jobs-with-bottom-nav">
      {/* Toast Notification */}
      {toast && (
        <div className={`jobs-toast jobs-toast-${toast.type}`}>
          <div className="jobs-toast-content">
            <div className="jobs-toast-icon">
              {toast.type === 'success' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              )}
              {toast.type === 'error' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              )}
              {toast.type === 'warning' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              )}
            </div>
            <div className="jobs-toast-message">
              <p>{toast.message}</p>
            </div>
            <button 
              className="jobs-toast-close"
              onClick={() => setToast(null)}
              aria-label="Close notification"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="jobs-toast-progress">
            <div className="jobs-toast-progress-bar"></div>
          </div>
        </div>
      )}
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="jobs-header-nav">
        <Header
          onLoginClick={onLoginClick || (() => navigate('/login'))}
          onSignupClick={onSignupClick || (() => navigate('/signup'))}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          onJobsClick={handleJobsClick}
          currentPage="vacancies"
        />
      </div>
      
      <div className="jobs-container">
        {/* Filters Section - Above Content */}
        <div className="jobs-filters-section">
          <div className="jobs-filters-header">
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              width: '100%'
            }}>
              {/* Saved Search Preferences and Bookmark Dropdowns - Left side of tabs */}
              {isAuthenticated && ((savedPreferences && savedPreferences.length > 0) || (bookmarkLists && bookmarkLists.length > 0)) && (
                <div style={{ 
                  display: 'flex', 
                  gap: '12px',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  alignItems: 'flex-start',
                  flex: isMobile ? '1 1 100%' : '0 0 auto'
                }}>
                  {/* Saved Search Preferences Dropdown */}
                  {savedPreferences && savedPreferences.length > 0 && (
                    <div style={{ 
                      flex: isMobile ? '1' : '0 0 auto', 
                      minWidth: isMobile ? '100%' : '200px',
                      maxWidth: isMobile ? '100%' : '280px',
                      width: isMobile ? '100%' : 'auto'
                    }}>
                      <label className="job-filter-section-header" style={{ marginBottom: '6px', fontSize: '12px' }}>
                        <span>Saved Search Preference</span>
                      </label>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={filters.preferenceId || ''}
                          onChange={(e) => {
                            handlePreferenceChange(e.target.value);
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 32px 8px 10px',
                            fontSize: '13px',
                            fontWeight: filters.preferenceId ? 600 : 500,
                            color: filters.preferenceId ? '#7a5fb8' : '#1f2937',
                            border: `1px solid ${filters.preferenceId ? '#7a5fb8' : '#e5e7eb'}`,
                            borderRadius: '6px',
                            background: filters.preferenceId ? 'linear-gradient(to bottom, #faf5ff, #f5f3ff)' : 'white',
                            cursor: 'pointer',
                            appearance: 'none',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <option value="">Select preference...</option>
                          {savedPreferences.map(pref => (
                            <option key={pref.id} value={pref.id}>
                              {pref.title} {!pref.is_active && '(Inactive)'}
                            </option>
                          ))}
                        </select>
                        <div style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6b7280' }}>
                            <polyline points="6,9 12,15 18,9"></polyline>
                          </svg>
                        </div>
                        {filters.preferenceId && filters.preferenceId !== '' && (
                          <button
                            onClick={() => handlePreferenceChange('')}
                            style={{
                              position: 'absolute',
                              right: '28px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              color: '#7a5fb8',
                              fontSize: '11px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              padding: '3px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'none';
                            }}
                            title="Clear preference filter"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Bookmark List Dropdown */}
                  {bookmarkLists && bookmarkLists.length > 0 && (
                    <div style={{ 
                      flex: isMobile ? '1' : '0 0 auto', 
                      minWidth: isMobile ? '100%' : '200px',
                      maxWidth: isMobile ? '100%' : '280px',
                      width: isMobile ? '100%' : 'auto'
                    }}>
                      <label style={{ 
                        fontSize: '12px', 
                        fontWeight: 600, 
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginBottom: '6px'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                        </svg>
                        Bookmark List:
                      </label>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={filters.bookmarkListId || ''}
                          onChange={(e) => {
                            handleBookmarkChange(e.target.value);
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 32px 8px 10px',
                            fontSize: '13px',
                            fontWeight: filters.bookmarkListId ? 600 : 500,
                            color: filters.bookmarkListId ? '#7a5fb8' : '#1f2937',
                            border: `1px solid ${filters.bookmarkListId ? '#7a5fb8' : '#e5e7eb'}`,
                            borderRadius: '6px',
                            background: filters.bookmarkListId ? 'linear-gradient(to bottom, #faf5ff, #f5f3ff)' : 'white',
                            cursor: 'pointer',
                            appearance: 'none',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <option value="">All Jobs</option>
                          {bookmarkLists.map(list => (
                            <option key={list.id} value={list.id}>
                              {list.title || 'Untitled List'} {list.vacancies_count > 0 && `(${list.vacancies_count})`}
                            </option>
                          ))}
                        </select>
                        <div style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6b7280' }}>
                            <polyline points="6,9 12,15 18,9"></polyline>
                          </svg>
                        </div>
                        {filters.bookmarkListId && filters.bookmarkListId !== '' && (
                          <button
                            onClick={() => handleBookmarkChange('')}
                            style={{
                              position: 'absolute',
                              right: '28px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              color: '#7a5fb8',
                              fontSize: '11px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              padding: '3px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'none';
                            }}
                            title="Clear bookmark filter"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Tabs - Center/Right */}
              <div className="jobs-page-tabs" style={{ marginLeft: 'auto' }}>
                <button 
                  className={`jobs-tab ${location.pathname === '/vacancies' ? 'active' : ''}`}
                  onClick={() => navigate('/vacancies')}
                >
                  All Jobs
                </button>
                <button 
                  className={`jobs-tab ${location.pathname === '/applied-jobs' ? 'active' : ''}`}
                  onClick={() => navigate('/applied-jobs')}
                >
                  My Applications
                </button>
              </div>
              
              {/* Right side buttons */}
              <div className="jobs-filters-header-right">
                {!isMobile && Object.values(appliedFilters).some(v => {
                  return v !== '' && v !== 'all';
                }) && (
                  <button 
                    className="jobs-filter-clear-all-btn"
                    onClick={handleClearFilters}
                  >
                    Clear all
                  </button>
                )}
                {/* Mobile Filter Icon Button */}
                {isMobile && (
                  <button 
                    className="jobs-filter-icon-btn"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="4" y1="21" x2="4" y2="14"></line>
                      <line x1="4" y1="10" x2="4" y2="3"></line>
                      <line x1="12" y1="21" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12" y2="3"></line>
                      <line x1="20" y1="21" x2="20" y2="16"></line>
                      <line x1="20" y1="12" x2="20" y2="3"></line>
                      <line x1="1" y1="14" x2="7" y2="14"></line>
                      <line x1="9" y1="8" x2="15" y2="8"></line>
                      <line x1="17" y1="16" x2="23" y2="16"></line>
                    </svg>
                    {Object.values(appliedFilters).some(v => {
                      return v !== '' && v !== 'all';
                    }) && <span className="jobs-filter-badge"></span>}
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Desktop Filters - Always visible */}
          {!isMobile && (
            <div className="jobs-filters-content">
              <JobFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onApplyFilters={handleApplyFilters}
                onSavePreference={handleSavePreference}
                jobCount={jobs.length}
                isHorizontal={true}
              />
            </div>
          )}
        </div>

        {/* Mobile Filter Modal/Popup */}
        {isMobile && showFilters && createPortal(
          <div className="jobs-filter-mobile-modal-overlay" onClick={() => setShowFilters(false)}>
            <div className="jobs-filter-mobile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="jobs-filter-mobile-modal-header">
                <h3>Filters</h3>
                <div className="jobs-filter-mobile-modal-header-right">
                  {Object.values(filters).some(v => {
                    return v !== '' && v !== 'all';
                  }) && (
                    <button 
                      className="jobs-filter-mobile-clear-btn"
                      onClick={handleClearFilters}
                    >
                      Clear all
                    </button>
                  )}
                  <button 
                    className="jobs-filter-mobile-close-btn"
                    onClick={() => setShowFilters(false)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="jobs-filter-mobile-modal-content" style={{ display: 'block', visibility: 'visible', minHeight: '300px', padding: '20px' }}>
                {/* Saved Search Preferences Dropdown for Mobile */}
                {isAuthenticated && savedPreferences && savedPreferences.length > 0 && (
                  <div style={{ 
                    marginBottom: '20px',
                    paddingBottom: '20px',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <label className="job-filter-section-header" style={{ marginBottom: '8px' }}>
                      <span>Saved Search Preference</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={filters.preferenceId || ''}
                        onChange={(e) => {
                          handlePreferenceChange(e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          fontSize: '14px',
                          fontWeight: filters.preferenceId ? 600 : 500,
                          color: filters.preferenceId ? '#7a5fb8' : '#1f2937',
                          border: `1px solid ${filters.preferenceId ? '#7a5fb8' : '#e5e7eb'}`,
                          borderRadius: '6px',
                          background: filters.preferenceId ? 'linear-gradient(to bottom, #faf5ff, #f5f3ff)' : 'white',
                          cursor: 'pointer',
                          appearance: 'none'
                        }}
                      >
                        <option value="">Select a saved preference...</option>
                        {savedPreferences.map(pref => (
                          <option key={pref.id} value={pref.id}>
                            {pref.title} {!pref.is_active && '(Inactive)'}
                          </option>
                        ))}
                      </select>
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6b7280' }}>
                          <polyline points="6,9 12,15 18,9"></polyline>
                        </svg>
                      </div>
                    </div>
                    {filters.preferenceId && filters.preferenceId !== '' && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: '#f5f3ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                        </svg>
                        <span>Filters applied from saved preference</span>
                      </div>
                    )}
                  </div>
                )}
                {filters && (
                  <JobFilters
                    filters={filters}
                    onFilterChange={(key, value) => {
                      handleFilterChange(key, value);
                    }}
                    onClearFilters={handleClearFilters}
                    onApplyFilters={handleApplyFilters}
                    onSavePreference={handleSavePreference}
                    jobCount={jobs.length}
                    isHorizontal={false}
                    hideHeader={true}
                  />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        <div className="jobs-layout">
          {/* Left Sidebar - Job List */}
          <div className="jobs-sidebar">

            {loading ? (
              <div className="jobs-loading">
                <div className="jobs-spinner"></div>
                <span>Loading jobs...</span>
              </div>
            ) : jobs.length === 0 ? (
              <div className="jobs-empty">
                <div className="jobs-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                </div>
                <h3>No jobs available</h3>
                <p>Check back later for new opportunities</p>
              </div>
            ) : (
              <div className="jobs-list">
                {jobs.map((job) => {
                  const isHidden = hiddenJobIds.has(job.id);
                  return (
                  <div
                    key={job.id}
                    className={`job-item ${selectedJob?.id === job.id ? 'active' : ''} ${isHidden ? 'reported-hidden' : ''}`}
                    onClick={(e) => handleJobSelect(job, e)}
                  >
                    {isHidden && (
                      <div className="job-item-overlay">
                        <div className="job-item-overlay-content">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                          </svg>
                          <span>Reported and hidden for you</span>
                          <button 
                            className="job-item-overlay-undo-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnhideJob(job.id);
                            }}
                            title="Undo - Show this job again"
                          >
                            Undo
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="job-item-header">
                      <h3 className="job-item-title">{job.title}</h3>
                      <span className="job-status-badge active">Active</span>
                    </div>
                    <div className="job-item-meta">
                      {(() => {
                        const label = getJobPosterLabel(job);
                        const posterUsername = getJobPosterUsername(job);
                        const linkPoster = shouldLinkJobPoster(job);

                        if (linkPoster && posterUsername) {
                          return (
                            <a href={`/talent/${posterUsername}`} target="_blank" rel="noopener noreferrer" className="job-company job-company-link">{label}</a>
                          );
                        }

                        return <span className={`job-company${job.is_managed_by_platform ? ' job-platform-badge' : ''}`}>{label}</span>;
                      })()}
                      {job.work_type && (
                        <>
                          <span className="job-separator">•</span>
                          <span className="job-type">{job.work_type}</span>
                        </>
                      )}
                    </div>
                    {job.application_count > 0 && (
                      <div className="job-applications">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        {job.application_count} {job.application_count === 1 ? 'application' : 'applications'}
                      </div>
                    )}
                    <div className="job-item-footer">
                      {formatSalary(job) && (
                        <span className="job-salary">{formatSalary(job)}</span>
                      )}
                      <span className="job-date">{formatDate(job.created_at)}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            
            {/* Load More Button */}
            {!loading && jobs.length > 0 && hasMoreJobs && (
              <div className="jobs-load-more-container">
                <button 
                  className="jobs-load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <div className="jobs-load-more-spinner"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <span>Load More</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <polyline points="19,12 12,19 5,12"></polyline>
                      </svg>
                    </>
                  )}
                </button>
                {totalJobs > 0 && (
                  <p className="jobs-load-more-info">
                    Showing {jobs.length} of {totalJobs} jobs
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Job Details */}
          <div className="jobs-detail-panel">
            {selectedJob ? (
              <div className="jobs-detail-panel-wrapper" style={{ position: 'relative' }}>
                {hiddenJobIds.has(selectedJob.id) && (
                  <div className="job-detail-overlay">
                    <div className="job-detail-overlay-content">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      <span>Reported and hidden for you</span>
                      <button 
                        className="job-detail-overlay-undo-btn"
                        onClick={() => handleUnhideJob(selectedJob.id)}
                        title="Undo - Show this job again"
                      >
                        Undo
                      </button>
                    </div>
                  </div>
                )}
                <PublicJobDetailView 
                  job={selectedJob} 
                  onApplyClick={handleApplyClick}
                  hasApplied={hasApplied}
                  currentUser={user}
                  isAuthenticated={isAuthenticated}
                  onBookmarkClick={() => setShowBookmarkModal(true)}
                  onJobReportSuccess={handleJobReportSuccess}
                />
              </div>
            ) : (
              <div className="jobs-no-selection">
                <div className="no-selection-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                </div>
                <h3>Select a job to view details</h3>
                <p>Choose a job from the list to see its details and apply</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Preference Modal */}
      {showSavePreferenceModal && createPortal(
        <div 
          className="jobs-filter-mobile-modal-overlay" 
          onClick={() => {
            setShowSavePreferenceModal(false);
            setPreferenceTitle('');
          }}
          style={{ 
            zIndex: 10000,
            alignItems: isMobile ? 'flex-end' : 'center',
            padding: isMobile ? '0' : '20px'
          }}
        >
          <div 
            className="jobs-filter-mobile-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              maxWidth: isMobile ? '100%' : '500px', 
              width: isMobile ? '100%' : '90%',
              borderRadius: isMobile ? '20px 20px 0 0' : '16px',
              maxHeight: isMobile ? '85vh' : '90vh',
              minHeight: isMobile ? '400px' : 'auto',
              animation: isMobile ? 'slideUpModal 0.3s ease forwards' : 'modalSlideIn 0.3s ease-out',
              boxShadow: isMobile ? '0 -4px 20px rgba(0, 0, 0, 0.15)' : '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="jobs-filter-mobile-modal-header">
              <h3>Save Search Preference</h3>
              <button 
                className="jobs-filter-mobile-close-btn"
                onClick={() => {
                  setShowSavePreferenceModal(false);
                  setPreferenceTitle('');
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="jobs-filter-mobile-modal-content" style={{ padding: '20px' }}>
              <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                Give your search preference a name so you can easily find and apply it later. You'll also receive notifications when new jobs match these filters.
              </p>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#374151' 
                }}>
                  Preference Title *
                </label>
                <input
                  type="text"
                  value={preferenceTitle}
                  onChange={(e) => setPreferenceTitle(e.target.value)}
                  placeholder="e.g., Remote React Jobs"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7a5fb8'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSavePreferenceConfirm();
                    }
                  }}
                />
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end' 
              }}>
                <button
                  onClick={() => {
                    setShowSavePreferenceModal(false);
                    setPreferenceTitle('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferenceConfirm}
                  disabled={!preferenceTitle.trim()}
                  style={{
                    padding: '10px 20px',
                    background: preferenceTitle.trim() ? '#7a5fb8' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: preferenceTitle.trim() ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (preferenceTitle.trim()) {
                      e.target.style.background = '#6b4fa0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (preferenceTitle.trim()) {
                      e.target.style.background = '#7a5fb8';
                    }
                  }}
                >
                  Save Preference
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <Footer />
      <BottomNavigationBar
        onHomeClick={handleHomeClick}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onForumClick={handleForumClick}
        onJobsClick={handleJobsClick}
        onLoginClick={onLoginClick || (() => navigate('/login'))}
        onSignupClick={onSignupClick || (() => navigate('/signup'))}
        isLoggedIn={isAuthenticated}
        user={user}
      />

      {showApplicationForm && selectedJob && (
        <JobApplicationForm
          job={selectedJob}
          isOpen={showApplicationForm}
          onClose={() => setShowApplicationForm(false)}
          onSuccess={handleApplicationSuccess}
        />
      )}

      {/* Mobile Job Detail Modal */}
      {showMobileModal && selectedJob && isMobile && createPortal(
        <div className="job-detail-mobile-modal-overlay" onClick={() => setShowMobileModal(false)}>
          <div className="job-detail-mobile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-detail-mobile-modal-header">
              <h2>Job Details</h2>
              <button 
                className="job-detail-mobile-modal-close"
                onClick={() => setShowMobileModal(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="job-detail-mobile-modal-content" style={{ position: 'relative' }}>
              {hiddenJobIds.has(selectedJob.id) && (
                <div className="job-detail-overlay">
                  <div className="job-detail-overlay-content">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span>Reported and hidden for you</span>
                    <button 
                      className="job-detail-overlay-undo-btn"
                      onClick={() => handleUnhideJob(selectedJob.id)}
                      title="Undo - Show this job again"
                    >
                      Undo
                    </button>
                  </div>
                </div>
              )}
              <PublicJobDetailView 
                job={selectedJob} 
                onApplyClick={handleApplyClick}
                hasApplied={hasApplied}
                currentUser={user}
                isAuthenticated={isAuthenticated}
                onBookmarkClick={() => setShowBookmarkModal(true)}
                onJobReportSuccess={handleJobReportSuccess}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bookmark Modal */}
      {showBookmarkModal && selectedJob && (
        <VacancyBookmarkModal
          isOpen={showBookmarkModal}
          onClose={() => setShowBookmarkModal(false)}
          jobId={selectedJob.id}
          jobTitle={selectedJob.title}
          onBookmarked={() => {
            setShowBookmarkModal(false);
            fetchBookmarkLists(); // Refresh bookmark lists to update counts
            // Force re-render of PublicJobDetailView to update bookmark status
            setSelectedJob({ ...selectedJob });
          }}
        />
      )}
    </div>
  );
};

// Public Job Detail View Component (without management controls)
const PublicJobDetailView = ({ job, onApplyClick, hasApplied, currentUser, isAuthenticated, onBookmarkClick, onJobReportSuccess }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  
  // Check if job is bookmarked when component mounts or job changes
  useEffect(() => {
    const checkBookmarkStatus = async () => {
      if (isAuthenticated && job?.id) {
        try {
          const response = await bookmarkAPI.checkVacancyBookmarked(job.id);
          if (response.success) {
            setIsBookmarked(response.is_bookmarked);
          }
        } catch (err) {
          console.error('Error checking bookmark status:', err);
        }
      }
    };
    
    checkBookmarkStatus();
  }, [job?.id, isAuthenticated]);
  
  const actingUserId = currentUser?.acting_user_id ?? currentUser?.id;
  const isJobOwner = currentUser && (
    job.posted_by_user_id === actingUserId ||
    job.posted_by?.id === actingUserId ||
    job.posted_by_user_id?.toString() === String(actingUserId)
  );
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatSalary = () => {
    if (!job.salary_min && !job.salary_max) {
      return 'Not specified';
    }
    
    const currency = job.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
    const period = job.salary_period === 'yearly' ? 'year' : 
                   job.salary_period === 'monthly' ? 'month' :
                   job.salary_period === 'hourly' ? 'hour' : job.salary_period;
    
    if (job.salary_min && job.salary_max) {
      return `${symbol}${job.salary_min.toLocaleString()} - ${symbol}${job.salary_max.toLocaleString()} per ${period}`;
    } else if (job.salary_min) {
      return `${symbol}${job.salary_min.toLocaleString()}+ per ${period}`;
    }
    return 'Not specified';
  };

  return (
    <div className="public-job-detail-view">
      <div className="job-detail-header">
        <div className="job-detail-title-section">
          <h1 className="job-detail-title">{job.title}</h1>
          <div className="job-detail-meta">
            {(() => {
              const label = getJobPosterLabel(job);
              const posterUsername = getJobPosterUsername(job);
              const linkPoster = shouldLinkJobPoster(job);

              if (linkPoster && posterUsername) {
                return (
                  <a href={`/talent/${posterUsername}`} target="_blank" rel="noopener noreferrer" className="job-detail-company job-detail-company-link">{label}</a>
                );
              }

              return <span className={`job-detail-company${job.is_managed_by_platform ? ' job-detail-platform-badge' : ''}`}>{label}</span>;
            })()}
            {job.work_type && (
              <>
                <span className="job-detail-separator">•</span>
                <span className="job-detail-type">{job.work_type}</span>
              </>
            )}
            {job.job_type && (
              <>
                <span className="job-detail-separator">•</span>
                <span className="job-detail-type">{job.job_type}</span>
              </>
            )}
          </div>
        </div>
        <div className="job-detail-actions">
          <div className="job-actions-group">
            {isJobOwner && (
              <div className="job-owner-message">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>Your Job Posting</span>
              </div>
            )}
            {!isJobOwner && isAuthenticated && (hasApplied ? (
              <button className="applied-button" disabled>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Applied
              </button>
            ) : isAuthenticated ? (
              <button 
                onClick={onApplyClick}
                className="apply-button"
              >
                Apply Now
              </button>
            ) : null)}
                    <button 
                      onClick={() => setShowShareModal(true)}
                      className="share-button"
                      title="Share this job"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                    </button>
                    {!isJobOwner && (
                      <button 
                        onClick={() => setShowReportModal(true)}
                        className="report-button"
                        title="Report this job"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                          <line x1="4" y1="22" x2="4" y2="15"></line>
                        </svg>
                      </button>
                    )}
                    {isAuthenticated && onBookmarkClick && (
                      <button 
                        onClick={onBookmarkClick}
                        className={`share-button ${isBookmarked ? 'bookmarked' : ''}`}
                        title={isBookmarked ? "Remove bookmark" : "Bookmark this job"}
                        style={isBookmarked ? { color: '#7a5fb8', borderColor: '#7a5fb8' } : {}}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={isBookmarked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>

      <div className="job-detail-content">
        <div className="job-detail-info">
          <div className="job-detail-section">
            <h3>Job Description</h3>
            <div className="job-detail-text">
              <JobDescriptionContent content={job.description} />
            </div>
          </div>

          {job.requirements && (
            <div className="job-detail-section">
              <h3>Requirements</h3>
              <div className="job-detail-text">
                <JobDescriptionContent content={job.requirements} emptyMessage="No requirements provided" />
              </div>
            </div>
          )}

          <div className="job-detail-section">
            <h3>Job Information</h3>
            <div className="job-info-grid">
              <div className="job-info-item">
                <span className="job-info-label">Salary</span>
                <span className="job-info-value">{formatSalary()}</span>
              </div>
              {job.experience_level && (
                <div className="job-info-item">
                  <span className="job-info-label">Experience Level</span>
                  <span className="job-info-value">{job.experience_level}</span>
                </div>
              )}
              {job.location && (
                <div className="job-info-item">
                  <span className="job-info-label">Location</span>
                  <span className="job-info-value">{job.location}</span>
                </div>
              )}
              {job.timezone && (
                <div className="job-info-item">
                  <span className="job-info-label">Timezone</span>
                  <span className="job-info-value">{job.timezone}</span>
                </div>
              )}
              {job.application_deadline && (
                <div className="job-info-item">
                  <span className="job-info-label">Application Deadline</span>
                  <span className="job-info-value">{formatDate(job.application_deadline)}</span>
                </div>
              )}
              <div className="job-info-item">
                <span className="job-info-label">Posted</span>
                <span className="job-info-value">{formatDate(job.created_at)}</span>
              </div>
            </div>
          </div>

          {job.skills_required && job.skills_required.length > 0 && (
            <div className="job-detail-section">
              <h3>Required Skills</h3>
              <div className="job-skills-list">
                {job.skills_required.map((skill, index) => (
                  <span key={index} className="job-skill-tag">{skill}</span>
                ))}
              </div>
            </div>
          )}

          {/* Share Modal */}
          <JobShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            jobId={job.id}
          />
          {!isJobOwner && (
            <ReportJobModal
              isOpen={showReportModal}
              onClose={() => setShowReportModal(false)}
              jobId={job.id}
              jobTitle={job.title}
              onSuccess={onJobReportSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsPage;

